import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Grid2, Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Form, Formik } from 'formik';
import { PricingSalesConditions } from 'web/src/pages/AddTerminalPricingPage/PricingSalesConditions';

import { navigate } from '@redwoodjs/router';
import { MetaTags } from '@redwoodjs/web';

import { InputBaseField } from 'src/components/common/form/FormInput';
import { SelectBaseField } from 'src/components/common/form/FormSelect';
import CountrySelectInput from 'src/components/CountrySelectInput/CountrySelectInput';
import MainCard from 'src/components/MainCard/MainCard';
import { GetProductColumnsWithAddButton } from 'src/components/ProductPricing';
import ProductSearchInput from 'src/components/ProductPricing/ProductSearchInput';
import { DEFAULT_BORDER } from 'src/constants';
import {
    contractTermsMenuItems,
    PricingType,
    SalesConditionsType,
    terminalPricingOfferValidationSchema,
} from 'src/hooks/usePricings';
import { usePricingsOffers } from 'src/hooks/usePricings/usePricingsOffers';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { isIreland } from 'src/lib/countryConfig';
import { supportedCountries } from 'src/utils/utils';

import PricingAccordion from './PricingAccordion';
import { TerminalPricingOfferPartnersModal } from './TerminalPricingPartners';

const AddTerminalPricingPage = () => {
    const columns = GetProductColumnsWithAddButton();

    const {
        paginationModel,
        setPrimaryProduct,
        primaryProductList,
        selectedPrimaryProductIds,
        secondaryProductList,
        selectedSecondaryProductIds,
        selectedProducts,
        selectedPartners,
        setSelectedPartners,
        errorMessage,
        open,
        expanded,
        showCustomTerm,
        initialValues,
        onSubmit,
        loadingCreatePricingOffer,
        handlePricingTypeOnChange,
        enableProductForPromo,
        handleSetSelectContractTerm,
        handleFindPrimaryProducts,
        handleSelectedPrimaryProductsIds,
        handleSelectedSecondaryProductsIds,
        handleAccordionChange,
        handleMultiplePricingOnChange,
        handleCloseModal,
        rowCountState,
        handlePaginationModelChange,
        handlePricingLimitChange,
    } = usePricingsOffers(null, PricingType.TERMINAL, false);

    const handleSearchPrimary = () => {
        handleFindPrimaryProducts({ page: paginationModel });
    };

    return (
        <PageContent heading="Add Terminal Pricing offers">
            <MetaTags title="Terminal Pricing Offer" description="Create Terminal Pricing Offer" />
            <Formik
                initialValues={initialValues}
                onSubmit={(_values) => {}}
                validationSchema={terminalPricingOfferValidationSchema}
                enableReinitialize
            >
                {(formikProps) => {
                    const { values, setFieldValue, dirty, isValid, errors } = formikProps;
                    return (
                        <Form>
                            <Stack spacing={5}>
                                <MainCard>
                                    <h2 className="font-normal mb-0 mt-10">
                                        Pricing Offer Information
                                    </h2>
                                    <Box className="mt-20">
                                        <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                                            <Box>
                                                <Grid2 container spacing={3}>
                                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                                        <InputBaseField
                                                            label="Pricing Offer Name"
                                                            placeholder="Enter Pricing offer name"
                                                            name="name"
                                                        />
                                                    </Grid2>
                                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                                        <SelectBaseField
                                                            label="Contract Term"
                                                            name="term"
                                                            inputLabel="Select Contract Term"
                                                            menuItems={contractTermsMenuItems}
                                                            setSelectedValue={
                                                                handleSetSelectContractTerm
                                                            }
                                                        />
                                                    </Grid2>
                                                </Grid2>
                                                <Grid2
                                                    container
                                                    spacing={3}
                                                    sx={{ marginTop: '24px' }}
                                                >
                                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                                        <CountrySelectInput
                                                            setFieldValue={setFieldValue}
                                                            filterByAlpha2Codes={supportedCountries(
                                                                isIreland
                                                            )}
                                                            fieldName="country"
                                                            label="Select Country"
                                                            placeholder="Select Country"
                                                        />
                                                    </Grid2>
                                                </Grid2>
                                            </Box>
                                            <Box>
                                                <Grid2 container spacing={3}>
                                                    {showCustomTerm && (
                                                        <Grid2 item size={{ xs: 12, md: 6 }}>
                                                            <InputBaseField
                                                                placeholder="Enter Custom value"
                                                                type="number"
                                                                label="Custom Term"
                                                                name="customTerm"
                                                            />
                                                        </Grid2>
                                                    )}
                                                </Grid2>
                                            </Box>
                                            <Box>
                                                <h3 style={{ color: '#F18F34', marginTop: '30px' }}>
                                                    Primary Product
                                                </h3>
                                                <Stack>
                                                    <label className="font-bold mt-5">
                                                        Search Primary Product
                                                    </label>
                                                    <Box className="mt-10">
                                                        <ProductSearchInput
                                                            onSearch={handleSearchPrimary}
                                                            onInputChange={setPrimaryProduct}
                                                            errorMessage={errorMessage}
                                                        />
                                                    </Box>
                                                    {errorMessage && (
                                                        <label className="error">
                                                            {errorMessage}
                                                        </label>
                                                    )}
                                                    {primaryProductList.length > 0 && (
                                                        <Box
                                                            sx={{
                                                                marginTop: '20px',
                                                                border: 'thin solid #F18F34',
                                                                borderRadius: '10px',
                                                            }}
                                                        >
                                                            <DataGrid
                                                                sx={{
                                                                    '.MuiDataGrid-columnHeaders': {
                                                                        backgroundColor: '#F39E4E',
                                                                        color: '#ffffff',
                                                                        borderRadius:
                                                                            '10px 10px 0 0',
                                                                    },
                                                                }}
                                                                autoHeight
                                                                rows={primaryProductList}
                                                                columns={columns}
                                                                rowCount={rowCountState}
                                                                getRowHeight={() => {
                                                                    return 'auto';
                                                                }}
                                                                paginationModel={paginationModel}
                                                                paginationMode="server"
                                                                disableColumnMenu
                                                                disableColumnFilter
                                                                onPaginationModelChange={
                                                                    handlePaginationModelChange
                                                                }
                                                                pageSizeOptions={[5, 10, 25]}
                                                                onRowSelectionModelChange={(
                                                                    ids
                                                                ) => {
                                                                    handleSelectedPrimaryProductsIds(
                                                                        ids
                                                                    );
                                                                }}
                                                            />
                                                        </Box>
                                                    )}
                                                </Stack>
                                                {secondaryProductList.length > 0 && (
                                                    <Stack>
                                                        <h3
                                                            className="font-bold mt-40 mb-10"
                                                            style={{ color: '#FDC800' }}
                                                        >
                                                            Secondary Product
                                                        </h3>
                                                        <Box
                                                            sx={{
                                                                ...DEFAULT_BORDER,
                                                            }}
                                                        >
                                                            <DataGrid
                                                                autoHeight
                                                                rows={secondaryProductList}
                                                                columns={columns}
                                                                autoPageSize
                                                                pageSizeOptions={[5]}
                                                                getRowHeight={() => {
                                                                    return 'auto';
                                                                }}
                                                                paginationModel={paginationModel}
                                                                disableColumnFilter
                                                                disableColumnMenu
                                                                onRowSelectionModelChange={(
                                                                    ids
                                                                ) => {
                                                                    return handleSelectedSecondaryProductsIds(
                                                                        ids
                                                                    );
                                                                }}
                                                            />
                                                        </Box>
                                                    </Stack>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Box>
                                </MainCard>
                                {selectedProducts.length > 0 && (
                                    <>
                                        <MainCard>
                                            <Box className="plain-bottom-border">
                                                <h2 className="font-normal mb-0 mt-10">Pricing</h2>
                                            </Box>
                                            {selectedPrimaryProductIds.length > 0 && (
                                                <Box sx={{ marginTop: '30px' }}>
                                                    <h3 style={{ color: '#F18F34' }}>
                                                        Primary Product
                                                    </h3>
                                                    <PricingAccordion
                                                        products={selectedProducts.filter(
                                                            (product) => {
                                                                return (
                                                                    product.catalogue === 'PRIMARY'
                                                                );
                                                            }
                                                        )}
                                                        handleChange={handleAccordionChange}
                                                        expanded={expanded}
                                                        handlePricingTypeOnChange={
                                                            handlePricingTypeOnChange
                                                        }
                                                        handleMultiplePricingOnChange={(price) => {
                                                            return handleMultiplePricingOnChange(
                                                                price
                                                            );
                                                        }}
                                                        handleApplyPromo={enableProductForPromo}
                                                        handlePricingLimitChange={
                                                            handlePricingLimitChange
                                                        }
                                                        styling="primary"
                                                    />
                                                </Box>
                                            )}

                                            {selectedSecondaryProductIds.length > 0 && (
                                                <Box sx={{ marginTop: '30px' }}>
                                                    <h3 style={{ color: '#FDC800' }}>
                                                        Secondary Product
                                                    </h3>
                                                    <PricingAccordion
                                                        products={selectedProducts.filter(
                                                            (product) => {
                                                                return (
                                                                    product.catalogue ===
                                                                    'SECONDARY'
                                                                );
                                                            }
                                                        )}
                                                        handleChange={handleAccordionChange}
                                                        expanded={expanded}
                                                        handlePricingTypeOnChange={
                                                            handlePricingTypeOnChange
                                                        }
                                                        handleMultiplePricingOnChange={(price) => {
                                                            return handleMultiplePricingOnChange(
                                                                price
                                                            );
                                                        }}
                                                        handleApplyPromo={enableProductForPromo}
                                                        handlePricingLimitChange={
                                                            handlePricingLimitChange
                                                        }
                                                        styling="secondary"
                                                    />
                                                </Box>
                                            )}
                                        </MainCard>
                                        <PricingSalesConditions
                                            salesConditions={values.salesConditions}
                                            gpvConditions={values.gpvConditions}
                                            mccConditions={values.mccConditions}
                                            updatePricingConditions={(
                                                fieldName: string,
                                                fieldValue: SalesConditionsType
                                            ) => {
                                                return setFieldValue(fieldName, fieldValue);
                                            }}
                                        />
                                    </>
                                )}

                                <TerminalPricingOfferPartnersModal
                                    open={open}
                                    handleCloseModal={handleCloseModal}
                                    selectedPartners={selectedPartners}
                                    setSelectedPartners={setSelectedPartners}
                                />

                                <Box>
                                    <div className="flex space-end">
                                        <Button
                                            onClick={() => {
                                                return navigate('/terminal-pricing');
                                            }}
                                            className="reset-btn btn-medium mr-20"
                                            variant="contained"
                                            size="medium"
                                            disableElevation
                                        >
                                            Cancel
                                        </Button>
                                        {loadingCreatePricingOffer ? (
                                            <LoadingButton
                                                className="btn-medium"
                                                style={{
                                                    backgroundColor: '#F18F34',
                                                    color: '#fff',
                                                    padding: '5px 30px 5px 10px',
                                                }}
                                                loading
                                                loadingPosition="start"
                                                startIcon={<SaveIcon />}
                                                variant="contained"
                                            >
                                                Adding Terminal Offer
                                            </LoadingButton>
                                        ) : (
                                            <Box className="btn-wrapper">
                                                <Button
                                                    className={`${
                                                        dirty && isValid && errors
                                                            ? 'btn-primary'
                                                            : 'btn-dark'
                                                    } btn-medium`}
                                                    type="button"
                                                    variant="contained"
                                                    size="medium"
                                                    disableElevation
                                                    onClick={async () => {
                                                        return onSubmit(values);
                                                    }}
                                                >
                                                    Add Terminal Offer
                                                </Button>
                                            </Box>
                                        )}
                                    </div>
                                </Box>
                            </Stack>
                        </Form>
                    );
                }}
            </Formik>
        </PageContent>
    );
};

export default AddTerminalPricingPage;
